import * as React from 'react';
import { observable, computed } from 'mobx';
import { FormState, FieldState } from 'formstate';
import { lazyInject } from 'src/invoice/ioc';
import { InvoiceStore } from 'src/invoice/stores/invoice.store';
import { RouterStore } from 'src/core/stores/router.store';
import { FormValidators } from '@servicetitan/form';
import { BusyFormState } from 'src/util/busy-form';
import { ClientDataStore } from '../../../../core/stores/client-data.store';
import { statementContainer } from '../../../../statement/ioc';
import { ChargeStore } from '../../../../statement/stores/charge.store';
import { BankAccountInfo } from 'src/api';

export const BankAccountType = {
    Business: 'business',
    Personal: 'personal',
};

export class AchInfoFormState extends BusyFormState {
    @lazyInject(InvoiceStore) private invoiceStore!: InvoiceStore;
    @lazyInject(RouterStore) private router!: RouterStore;
    @lazyInject(ClientDataStore) private clientData!: ClientDataStore;

    @observable errorMessage: string | undefined;

    @observable modalIsOpen = false;
    @observable isProcessing = false;

    currentInputAccountNumber: string = '';
    accountType = new FieldState('').validators(FormValidators.required);
    accountName = new FieldState('').validators(FormValidators.required);

    routingNumber = new FieldState('').validators((value) => {
        if (!value) {
            return 'Field is required.';
        }
        const valid = value.length === 9 && value.replace(/\D/g, '').length === value.length;
        return valid ? null : 'Routing number is 9 digits.';
    });

    accountNumber = new FieldState('').validators((value) => {
        if (!value) {
            return 'Field is required.';
        }
        const valid =
            value.length > 3 &&
            value.length < 18 &&
            value.replace(/\D/g, '').length === value.length;
        // Here the value hasn't been assigned to accountNumber, so need the intermediate value to validate the confirmAccountNumber.
        this.currentInputAccountNumber = value;
        this.form.$.confirmAccountNumber.validate();
        return valid ? null : 'Account number is between 4 and 17 digits.';
    });

    confirmAccountNumber = new FieldState('').validators((value) => {
        if (!value) {
            return 'Field is required.';
        }
        const valid = value === this.currentInputAccountNumber;
        return valid ? null : 'Must match account number.';
    });

    @computed get phone() {
        return this.clientData.portalData ? this.clientData.portalData.brandData.phone : undefined;
    }

    @computed get companyName() {
        return this.clientData.portalData
            ? this.clientData.portalData.brandData.officialName
            : undefined;
    }

    form = new FormState({
        accountType: this.accountType,
        accountName: this.accountName,
        routingNumber: this.routingNumber,
        accountNumber: this.accountNumber,
        confirmAccountNumber: this.confirmAccountNumber,
        isBusy: this.isBusy,
    });

    constructor() {
        super();
        const payment = this.invoiceStore.payment;
        this.form.validate();
        if (!payment) {
            return;
        }

        if (!payment.bankAccount) {
            payment.bankAccount = {};
        }

        const bankAccount = payment.bankAccount;

        this.accountType.reset('');
        this.accountName.reset(bankAccount.name || '');
        this.routingNumber.reset(bankAccount.routing || '');
        this.accountNumber.reset(bankAccount.number || '');
        this.confirmAccountNumber.reset('');
    }

    clearError = () => {
        this.errorMessage = undefined;
    };

    changeAccountType = (e: any, val: string) => {
        this.accountType.reset(val);
    };

    closeConfirmationModal = () => {
        this.modalIsOpen = false;
    };

    showConfirmationModal = () => {
        this.modalIsOpen = true;
    };

    handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
        e && e.preventDefault();
        this.closeConfirmationModal();
        const payment = this.invoiceStore.payment;
        await this.form.validate();
        if (this.form.hasError) {
            return;
        }
        const bankAccount: BankAccountInfo = {
            name: this.accountName.$.trim(),
            routing: this.routingNumber.$.replace(/[^0-9]/g, ''),
            number: this.accountNumber.$.replace(/[^0-9]/g, ''),
            checkNumber: '',
        };
        if (payment) {
            payment.bankAccount = bankAccount;
            this.withBusy(
                this.invoiceStore.submitAchPayment(
                    payment,
                    this.accountType.$.trim() === BankAccountType.Business
                )
            ).then(
                (confirmation) => {
                    this.router.history.push(
                        `/invoice/${payment.invoiceNumber}/confirm-payment/${confirmation.number}`
                    );
                },
                (err) => {
                    this.errorMessage = err;
                }
            );
        } else {
            this.isProcessing = true;
            const { processMultiBankAccount } = statementContainer.get(ChargeStore);
            await processMultiBankAccount(
                bankAccount,
                this.accountType.$.trim() === BankAccountType.Business
            );
            this.isProcessing = false;
        }
    };
}
